import {
  InteractionRequiredAuthError,
  BrowserAuthError,
  type IPublicClientApplication,
  type SilentRequest,
} from '@azure/msal-browser';

/** MSAL error code set when a previous interactive flow has not yet settled. */
const INTERACTION_IN_PROGRESS = 'interaction_in_progress';

/**
 * Starts an interactive redirect only when no other MSAL interaction is
 * already in progress. Silently swallows the `interaction_in_progress`
 * error so that concurrent callers (e.g. parallel axios requests that all
 * try to recover a token on first paint) do not surface noisy toasts.
 */
async function safeAcquireTokenRedirect(
  instance: IPublicClientApplication,
  request: Parameters<IPublicClientApplication['acquireTokenRedirect']>[0],
): Promise<void> {
  // Honor the global redirect rate-limit installed by main.tsx. Without it,
  // a burst of API 401s on first paint can each trigger a separate
  // acquireTokenRedirect, producing a redirect storm.
  const guard = (typeof window !== 'undefined'
    ? (window as unknown as { __dclRedirectGuard?: { isLocked: () => boolean; mark: () => void } })
        .__dclRedirectGuard
    : undefined);
  if (guard?.isLocked()) {
    return;
  }
  guard?.mark();
  try {
    await instance.acquireTokenRedirect(request);
  } catch (e) {
    if (e instanceof BrowserAuthError && e.errorCode === INTERACTION_IN_PROGRESS) {
      return; // redirect already underway — no action needed
    }
    throw e;
  }
}

/**
 * Acquires an access token for the given scopes against the
 * current MSAL active account.
 *
 * Strategy:
 *   1. `acquireTokenSilent` with the active account.
 *   2. On `InteractionRequiredAuthError` (or `no_account` / silent
 *      failure without an active account) fall back to
 *      `acquireTokenRedirect`, which navigates away — the returned
 *      promise then never resolves in the current page context.
 *
 * Any other error is re-thrown so callers can decide whether to
 * surface a toast / translate into a domain error.
 */
export async function getAccessToken(
  instance: IPublicClientApplication,
  scopes: readonly string[],
): Promise<string> {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;

  if (!account) {
    // No account at all — trigger an interactive login so the user
    // can choose one. `loginRedirect` covers this path explicitly.
    await safeAcquireTokenRedirect(instance, { scopes: [...scopes] });
    // `acquireTokenRedirect` navigates away before resolving.
    return Promise.reject(new Error('Redirecting for interactive login'));
  }

  const silentRequest: SilentRequest = {
    account,
    scopes: [...scopes],
  };

  try {
    const result = await instance.acquireTokenSilent(silentRequest);
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await safeAcquireTokenRedirect(instance, { account, scopes: [...scopes] });
      return Promise.reject(error);
    }
    throw error;
  }
}
